/**
 * usePlanBuilder
 *
 * Derives plan builder state from usePatient + useHomepageState.
 * Patient fields come from the patient record; builder-only fields
 * (pastCycles, modalities, knownResults, freeText) from the homepage-state API.
 */

import { usePatient } from "@src/packages/patients/hooks/usePatient";
import {
  getPlanBuilderState,
  useHomepageState,
} from "@src/packages/patients/hooks/useHomepageState";
import type { Patient } from "@louise/patients/patients.types";
import type { PlanBuilderState, Condition, TreatmentModality } from "./types";
import { INITIAL_BUILDER_STATE } from "./types";
import type { AcceptedTreatmentPlan } from "../treatmentPlanTypes";
import { useTreatmentPlan } from "../hooks/useTreatmentPlan";
import type { HomepageGoal } from "../../homepage.types";

function isPlanBuilderTreatmentModality(
  value: string,
): value is TreatmentModality {
  switch (value) {
    case "ivf":
    case "iui":
    case "fet":
    case "not-sure":
      return true;
    default:
      return false;
  }
}

function deriveFromPatient(
  patient: Patient | undefined,
  acceptedPlan: AcceptedTreatmentPlan | null | undefined,
): PlanBuilderState {
  const base = { ...INITIAL_BUILDER_STATE };

  if (acceptedPlan) {
    base.acceptedPlan = {
      treatmentFamily: acceptedPlan.treatmentFamily,
      treatmentDestination: acceptedPlan.treatmentDestination,
      ovulationApproach: acceptedPlan.ovulationApproach,
      fertilizationMethod: acceptedPlan.fertilizationMethod,
      eggSource: acceptedPlan.eggSource,
      eggState: acceptedPlan.eggState,
      spermSource: acceptedPlan.spermSource,
      spermState: acceptedPlan.spermState,
      transferPlan: acceptedPlan.transferPlan,
      pgtPlan: acceptedPlan.pgtPlan,
      decisionSource: acceptedPlan.decisionSource,
    };
    base.acceptedPlanAcknowledged = true;
  }

  if (!patient) return base;

  if (patient.gender === "AFAB") base.gender = "female";
  else if (patient.gender === "AMAB") base.gender = "male";

  base.stage = patient.fertilityIntent?.stage ?? null;

  if (patient.dateOfBirth) {
    base.dateOfBirth = patient.dateOfBirth;
  }

  const goal = patient.fertilityIntent?.goal;
  if (goal) {
    const goalMap: Record<string, HomepageGoal> = {
      "egg-preserve": "preserve",
      "sperm-preserve": "preserve",
      "egg-freezing": "preserve",
      "sperm-freezing": "preserve",
      "embryo-preserve": "embryo-preserve",
      "embryo-freezing": "embryo-preserve",
      pregnancy: "pregnancy",
      "egg-donation": "egg-donation",
      "sperm-donation": "sperm-donation",
      exploring: "pregnancy",
      adoption: "adoption",
      surrogacy: "surrogacy",
    };
    base.goal = goalMap[goal] ?? null;
  }

  const DISEASE_CONDITIONS: Record<string, Condition> = {
    pcos: "pcos",
    endometriosis: "endometriosis",
    thyroid_disease: "thyroid_disease",
    diminished_ovarian_reserve: "diminished_ovarian_reserve",
    primary_ovarian_insufficiency: "primary_ovarian_insufficiency",
    fibroids: "fibroids",
  };
  // Conditions written as disease:"other" stash the original UI id in `other`
  // (the deprecated `description` write field is redirected to `other`
  // server-side). Reconstruct the UI condition so the round-trip is symmetric.
  const OTHER_CONDITIONS: Record<string, Condition> = {
    "male-factor": "male-factor",
    other: "other",
  };
  for (const d of patient.medicalHistory?.diseases ?? []) {
    if (!d.disease) continue;
    const c =
      d.disease === "other"
        ? d.other
          ? OTHER_CONDITIONS[d.other]
          : undefined
        : DISEASE_CONDITIONS[d.disease];
    if (c) base.conditions.push(c);
  }

  if (patient.ethnicity) {
    base.ethnicity = patient.ethnicity.split(", ").filter(Boolean);
  }

  // The retired plan builder only understands the legacy three-state answer.
  // Keep onboarding's explicit uncertainty distinct in the patient record,
  // but do not force it into a more specific builder state.
  if (patient.partnerChoice && patient.partnerChoice !== "unsure") {
    base.partnerStatus = patient.partnerChoice;
  }

  const address = patient.addresses?.[0];
  if (address?.postalCode) {
    base.zipCode = address.postalCode;
  }

  if (patient.fertilityIntent?.hasEmployerBenefits != null) {
    base.hasEmployerBenefit = patient.fertilityIntent.hasEmployerBenefits;
    base.employerBenefit = patient.fertilityIntent.employerBenefit ?? null;
  }

  if (patient.fertilityIntent?.plannedModalities) {
    base.modalities = patient.fertilityIntent.plannedModalities.filter(
      isPlanBuilderTreatmentModality,
    );
  }

  if (patient.timeline) {
    base.timeline = patient.timeline;
  }

  const stage = patient.fertilityIntent?.stage;
  if (stage === "exploring-options") {
    base.clinicStatus = "researching";
  } else if (stage === "getting-ready" || stage === "in-treatment") {
    base.clinicStatus = "at-clinic";
  }

  return base;
}

function pickBuilderFields(
  partial: Partial<PlanBuilderState>,
): Partial<PlanBuilderState> {
  const result: Partial<PlanBuilderState> = {};
  if ("goal" in partial) result.goal = partial.goal;
  if ("hasPastCycles" in partial) result.hasPastCycles = partial.hasPastCycles;
  if ("pastCycles" in partial) result.pastCycles = partial.pastCycles;
  if ("modalities" in partial) result.modalities = partial.modalities;
  if ("acceptedPlan" in partial) result.acceptedPlan = partial.acceptedPlan;
  if ("acceptedPlanAcknowledged" in partial) {
    result.acceptedPlanAcknowledged = partial.acceptedPlanAcknowledged;
  }
  if ("knownResults" in partial) result.knownResults = partial.knownResults;
  if ("freeText" in partial) result.freeText = partial.freeText;
  if ("conditions" in partial) result.conditions = partial.conditions;
  return result;
}

export function usePlanBuilder() {
  const { data: patient } = usePatient();
  const { data: acceptedPlan } = useTreatmentPlan();
  const { data: homepageState, set: setHomepageState } = useHomepageState();

  const saved = getPlanBuilderState(homepageState);
  const state: PlanBuilderState = {
    ...deriveFromPatient(patient, acceptedPlan),
    ...saved,
  };

  return {
    state,
    updateState(partial: Partial<PlanBuilderState>) {
      const builderOnly = pickBuilderFields(partial);
      if (Object.keys(builderOnly).length > 0) {
        setHomepageState({ planBuilderState: { ...saved, ...builderOnly } });
      }
    },
    resetState() {
      setHomepageState({ planBuilderState: null });
    },
  };
}
